"use client";

import type { WorkplaceRequestStatus } from "@gettactic/api";
import { StatusBadge } from "@gettactic/components/src/data-display/StatusBadge/StatusBadge";
import { cn } from "@gettactic/helpers";
import React from "react";

export type RequestStatusColor = React.ComponentProps<
  typeof StatusBadge
>["color"];

export const getRequestStatusColor = (
  status: WorkplaceRequestStatus | string
): RequestStatusColor => {
  switch (status) {
    case "pending":
      return "orange";
    case "closed":
      return "green";
    default:
      return "neutral";
  }
};

// Tinted-pill background/text per status color. Requests use orange, green and
// neutral; red is for failed webhook deliveries. Anything else falls back to neutral.
const PILL_TINT: Partial<
  Record<NonNullable<RequestStatusColor>, { bg: string; text: string }>
> = {
  orange: { bg: "bg-amber-50", text: "text-amber-700" },
  green: { bg: "bg-green-50", text: "text-green-700" },
  red: { bg: "bg-red-50", text: "text-red-700" },
  neutral: { bg: "bg-neutral-100", text: "text-neutral-600" }
};

interface RequestStatusPillProps {
  color: RequestStatusColor;
  label: string;
  className?: string;
}

/**
 * Tinted pill showing a workplace request's status. Shared by the queue table
 * and the request lists shown inside reservations.
 */
export const RequestStatusPill: React.FC<RequestStatusPillProps> = ({
  color,
  label,
  className
}) => {
  const tint = PILL_TINT[color ?? "neutral"] ?? PILL_TINT.neutral;
  return (
    <StatusBadge
      size="sm"
      color={color}
      label={label}
      className={cn("rounded-full px-2 py-1", tint?.bg, className)}
      labelClassName={cn("font-medium", tint?.text)}
    />
  );
};
